import React, { createContext, useContext, useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { useQueryClient } from '@tanstack/react-query';
import { camelizeKeys, decamelize } from 'humps';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { useShare } from '../hooks';
import { useFindEntityMatchesMutation } from '../hooks/useFindEntityMatchesMutation';
import { useMatchEntityMutation } from '../hooks/useMatchEntityMutation';

import { Answer, Question } from '../models';
import { useNotifications, useSidebar } from '../../../providers';
import { useUsers, useSources, useOrganizations } from '../../organization/providers';
import { useAuth } from '../../authentication';
import { useConversations } from './ConversationsProvider';
import { useQuestions } from './QuestionsProvider';
import { useTermTranslations } from './TermTranslationsProvider';
import { useSpaces } from './SpacesProvider';
import { useAnswers } from './AnswersProvider';
import { useDatabaseDescription } from './DatabaseDescriptionProvider';
import { useSocket } from '../../../hooks';
import { mergeSourcesIntoColumns } from '../../../utils';

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const findQuestionById = (questions, questionId) => {
  if (!questions) {
    return null;
  }
  let foundQuestion = null;

  questions.forEach((question) => {
    if (foundQuestion) return;

    // Direct match check
    if (question.id === questionId) {
      foundQuestion = question;
      return;
    }

    // Check if the question is an array - react-query data.
    if (Array.isArray(question.followUps)) {
      if (question.followUps.length > 0) {
        const result = findQuestionById(question.followUps, questionId);
        if (result) {
          foundQuestion = result;
        }
      }
      // We are dealing with an immutable map of Question objects
    } else if (List.isList(question.followUps)) {
      if (question.followUps.size > 0) {
        const result = findQuestionById(question.followUps.toArray(), questionId);
        if (result) {
          foundQuestion = result;
        }
      }
    }
  });

  return foundQuestion;
};

const isQuestionInPath = (path, questionId) => {
  if (!path) {
    return false;
  }

  if (path.has(questionId)) {
    return true;
  }

  const next = path.get(path?.keySeq().toArray()[0]);

  return isQuestionInPath(next, questionId);
};

const updateQuestionParams = (questions, questionId, params) => {
  const updateQuestionData = (questionList) =>
    questionList?.map((question) => {
      if (question.id === questionId) {
        return {
          ...question,
          ...params
        };
      }

      if (question.followUps) {
        return {
          ...question,
          followUps: updateQuestionData(question.followUps)
        };
      }

      return question;
    });

  return updateQuestionData(questions);
};

const replaceQuestion = (questions, questionId, updatedQuestion) => {
  const replaceQuestionData = (questionList) =>
    questionList.map((question) => {
      if (question.id === questionId) {
        return { ...updatedQuestion };
      }

      if (question.followUps) {
        return {
          ...question,
          followUps: replaceQuestionData(question.followUps)
        };
      }

      return question;
    });

  return replaceQuestionData(questions);
};

const processMatchingEntities = (matchingEntities, body) => {
  if (!matchingEntities || matchingEntities.length === 0) return null;

  return matchingEntities
    .map(({ entity, suggestions }) => {
      const lowerCaseEntity = entity.toLowerCase();
      const escapedEntity = escapeRegExp(lowerCaseEntity);

      const regex = new RegExp(`(^|\\W)${escapedEntity}(\\W|$)`, 'i');

      if (!regex.test(body)) return null;

      const updatedSuggestions = suggestions?.map((s) => {
        const lowerCaseText = s.text.toLowerCase();
        return lowerCaseText === lowerCaseEntity ? { ...s, score: 0 } : s;
      });

      return { entity, suggestions: updatedSuggestions };
    })
    .filter(Boolean);
};

const updateParentQuestionWithChild = (questions, parentQuestionId, childQuestion) => {
  const updateParentData = (questionList) =>
    questionList?.map((question) => {
      if (question.id === parentQuestionId) {
        const existingChildIndex = question.childQuestions?.findIndex(
          (child) => child.id === childQuestion.id
        );

        if (existingChildIndex !== -1) {
          // Replace existing child question
          const updatedChildQuestions = [...(question.childQuestions || [])];
          updatedChildQuestions[existingChildIndex] = childQuestion;
          return {
            ...question,
            childQuestions: updatedChildQuestions
          };
        }
        // Add new child question
        return {
          ...question,
          childQuestions: [...(question.childQuestions || []), childQuestion]
        };
      }

      return question;
    });

  return updateParentData(questions);
};

const AssetBuilderContext = createContext();

const AssetBuilderProvider = ({ children = null }) => {
  // #region Hooks
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams();
  const { setSidebarCollapsed } = useSidebar();
  const queryClient = useQueryClient();
  // #endregion
  // #region Internal state
  const [isChartMode, setIsChartMode] = useState(false);
  const [selectionPath, setSelectionPath] = useState(new Map());
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [matchingEntities, setMatchingEntities] = useState([]);
  // #endregion

  // #region Data providers
  const { currentUser, users, activeUsers } = useUsers();
  const {
    currentConversation,
    updateConversation,
    touchConversation,
    deleteConversation,
    openNextConversation
  } = useConversations();
  const {
    questions,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    retryChatQuestion,
    isFetchingQuestions,
    assets,
    addQuestionToSpace,
    removeQuestionFromSpace,
    createAnswer
  } = useQuestions();
  const { sources } = useSources();
  const { addNotification } = useNotifications();
  const { termTranslations, metrics } = useTermTranslations();
  const { currentOrganization } = useOrganizations();
  const { databaseDescription } = useDatabaseDescription();
  const { spaces, createSpace } = useSpaces();
  const { updateAnswer } = useAnswers();

  // Add entity matching hooks
  const { mutateAsync: findEntityMatches } = useFindEntityMatchesMutation(accessToken);
  const { mutateAsync: matchEntity } = useMatchEntityMutation(accessToken);

  const { io, leaveRoom, joinRoom } = useSocket(accessToken, currentOrganization?.id, 'chat');
  // #endregion

  // #region Derived values
  const dbSchemasWithSources = useMemo(() => {
    if (!sources) return null;
    if (!databaseDescription) return null;

    return mergeSourcesIntoColumns(sources, databaseDescription);
  }, [sources, databaseDescription]);

  const columnMentionItems = useMemo(() => {
    if (!dbSchemasWithSources) return null;

    return dbSchemasWithSources
      .flatMap((schema) =>
        Object.values(schema.views).flatMap((view) => {
          const viewName = view.name?.split('.')?.[1] ? view.name?.split('.')?.[1] : view.name;
          return view.columns.map((column) => ({
            value: decamelize(`${schema.name}.${viewName}.${column.name}`),
            schema: decamelize(schema.name),
            view: decamelize(viewName),
            column: decamelize(column.name),
            columnDescription: column.description,
            viewDescription: view.description,
            schemaDescription: schema.description,
            enumeratedValues: column.enumeratedValues,
            source: column.source
          }));
        })
      )
      .sort((a, b) => a.value.localeCompare(b.column));
  }, [dbSchemasWithSources]);

  const participants = useMemo(() => {
    const sharedWith = [
      ...new Set(
        currentConversation?.shares?.flatMap((share) =>
          share.participants.map((participant) => participant.userId)
        )
      )
    ].map((id) => users.find((u) => u.id === id));

    if (sharedWith.length > 0) {
      sharedWith.unshift({
        id: currentConversation.userId,
        name: users.find((u) => u.id === currentConversation.userId)?.name,
        picture: users.find((u) => u.id === currentConversation.userId)?.picture
      });
    }

    if (currentConversation?.analystInvited) {
      sharedWith.unshift({
        id: 'sup-an',
        name: 'Supper Analyst',
        picture: null
      });
    }

    return sharedWith;
  }, [currentConversation?.shares, currentConversation?.analystInvited]);

  const conversationParticipants = useMemo(
    () =>
      activeUsers.filter((user) =>
        currentConversation?.shares[0]?.participants.some(
          (participant) => participant.userId === user.id
        )
      ),
    [currentConversation, activeUsers]
  );

  const shareableUsers = useMemo(
    () =>
      activeUsers.filter(
        (user) =>
          !currentConversation?.shares[0]?.participants.some(
            (participant) => participant.userId === user.id
          )
      ),
    [currentConversation, activeUsers]
  );

  const queryKey = useMemo(
    () => ['chat', 'questions', currentOrganization?.id, conversationId],
    [currentOrganization?.id, conversationId]
  );

  // #endregion

  // #region Callbacks & Functions
  const { onShare } = useShare(shareableUsers, conversationParticipants, currentConversation);

  const handleUserFeedback = useCallback(
    (question, revert = false) => {
      updateQuestion(
        {
          action: 'verify_answer',
          body: {
            userId: currentUser.id,
            answerId: question?.answer?.id,
            feedbackScore: revert ? 0 : -1,
            feedbackAnnotation: null
          }
        },
        question.id
      );

      if (revert) return;

      addNotification({
        message: 'A Supper analyst will review your question',
        icon: 'help-circle',
        undo: () => handleUserFeedback(question, true)
      });
    },
    [updateQuestion]
  );

  const handleUpdateAssetTitle = useCallback(
    async (question, assetTitle) => {
      const updatedQuestion = question.set('assetTitle', assetTitle);
      await updateQuestion({ body: { assetTitle }, action: 'update_asset_title' }, question.id);
      setSelectedQuestion(updatedQuestion);
    },
    [updateQuestion, setSelectedQuestion]
  );

  const handleUpdateAnswer = useCallback(
    async (id, answer) => {
      const updatedAnswer = await updateAnswer(id, answer);
      setSelectedQuestion((currentQuestion) =>
        currentQuestion.set('answers', [new Answer(updatedAnswer)])
      );
    },
    [updateAnswer]
  );

  const handleAddQuestionToSpace = useCallback(
    async (question, space) => {
      const updatedQuestion = question.set('spaceIds', [...question.spaceIds, space.id]);
      await addQuestionToSpace(question, space);
      setSelectedQuestion(updatedQuestion);
    },
    [addQuestionToSpace, setSelectedQuestion]
  );

  const handleRemoveQuestionFromSpace = useCallback(
    async (question, space) => {
      const updatedQuestion = question.set('spaceIds', [
        ...question.spaceIds.filter((spaceId) => spaceId !== space.id)
      ]);

      await removeQuestionFromSpace(question, space);
      setSelectedQuestion(updatedQuestion);
    },
    [removeQuestionFromSpace, setSelectedQuestion, spaces]
  );

  const handleRetryQuestion = useCallback(
    async (question) => {
      const updatedQuestion = question.set('answers', []);

      setSelectedQuestion(updatedQuestion);
      touchConversation(updatedQuestion.conversationId);

      const newQuestion = await retryChatQuestion(question.id);

      // retryQuestion function, it explicitly returns null when there's an error,
      //  then we don't want to set the selected question
      if (newQuestion) {
        setSelectedQuestion(new Question(newQuestion));
      }
    },
    [retryChatQuestion, setSelectedQuestion, selectedQuestion]
  );

  const handleCreateAnswer = useCallback(
    async (question, rawSql) => {
      // Optimistically clear answers in UI
      setSelectedQuestion(question.set('answers', []));
      touchConversation(question.conversationId);

      // Use shared createAnswer for cache updates
      const newQuestion = await createAnswer(question.id, rawSql);

      if (newQuestion.answers[0].id === selectedQuestion.answer.id) {
        // Error notification
        addNotification({
          message: 'We hit an error applying your changes. Reverted to last saved query.',
          icon: 'alert'
        });
        return false;
      }
      // Update UI with real answer
      setSelectedQuestion(new Question({ ...newQuestion, status: null }));
      return true;
    },
    [createAnswer, selectedQuestion, addNotification, touchConversation]
  );

  // Need to revisit the schema and ask for permission to see the q
  const handleShare = () =>
    onShare(currentConversation.id, 'conversation', currentConversation.schemas?.[0]);

  // #region Entity matching functions
  const handleFindEntityMatchesImpl = useCallback(
    async (inputStr) => {
      if (!accessToken || !currentOrganization?.id) return [];

      try {
        // eslint-disable-next-line no-unused-vars
        const result = await findEntityMatches({
          data: { input_str: inputStr },
          organizationId: currentOrganization.id
        });

        if (result && Array.isArray(result) && result.length > 0) {
          // Process each entity individually and merge results
          const matchResults = await matchEntity({
            entities: result,
            organizationId: currentOrganization.id
          });

          // Flatten the array of results
          const retVal = matchResults;

          // Set state for other components that might need it
          setMatchingEntities(retVal);

          // Return the entities for immediate use
          return retVal;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in entity matching:', error);
        return [];
      }
      return [];
    },
    [accessToken, currentOrganization?.id, findEntityMatches, matchEntity, matchingEntities]
  );

  // Create a memoized debounced version of the implementation
  const handleFindEntityMatches = useMemo(
    () => debounce(handleFindEntityMatchesImpl, 500),
    [handleFindEntityMatchesImpl]
  );

  // Clean up the debounce on unmount
  useEffect(
    () => () => {
      handleFindEntityMatches.cancel();
    },
    [handleFindEntityMatches]
  );

  const handleSelectEntity = useCallback(
    async (selectedText) => {
      // Then trigger new entity search for the selected text
      if (selectedText && selectedText.trim()) {
        try {
          await handleFindEntityMatchesImpl(selectedText);
        } catch (error) {
          // Silent error handling - entity search will retry naturally
        }
      }
    },
    [setMatchingEntities, handleFindEntityMatchesImpl]
  );
  // #endregion

  const handleCreateQuestion = useCallback(
    async (body, previousQuestionId = null, command = null, _conversationId = null) => {
      if (joinRoom) {
        joinRoom('conversation', _conversationId ?? conversationId);
      }

      // setTimeout(() => setSidebarCollapsed(true), 300);
      const _previousQuestionId =
        previousQuestionId === currentConversation?.assetQuestion?.id ? null : previousQuestionId;

      touchConversation(conversationId);

      const entities = await handleFindEntityMatchesImpl(body);

      await createQuestion(
        body,
        _conversationId ?? conversationId,
        _previousQuestionId,
        command,
        processMatchingEntities(entities, body.toLowerCase())
      );
      setMatchingEntities([]);
    },
    [
      createQuestion,
      touchConversation,
      setSidebarCollapsed,
      selectedQuestion,
      joinRoom,
      matchingEntities,
      handleFindEntityMatchesImpl,
      currentConversation
    ]
  );

  const buildSelectionPath = (question) => {
    if (question.previousQuestionId === null) {
      // If it is a root question, create a simple path
      return new Map([[question.id, new Map()]]);
    }

    // If it is not a root question, build the path recursively
    let currentQuestion = question;
    let path = new Map([[currentQuestion.id, new Map()]]);

    while (currentQuestion.previousQuestionId !== null) {
      const questionsQueryData = queryClient.getQueryData(queryKey);
      const previousQuestion = findQuestionById(
        questionsQueryData,
        currentQuestion.previousQuestionId
      );
      path = new Map([[previousQuestion.id, path]]);

      currentQuestion = previousQuestion;
    }

    return path;
  };

  const handleSelectQuestion = useCallback(
    (question, path = null) => {
      setSelectedQuestion(question);

      if (isQuestionInPath(selectionPath, question.id) && path === null) {
        if (location.hash.substring(1) !== question.id) {
          navigate(`?organization_id=${currentOrganization.id}#${question.id}`, { replace: true });
        }
        return;
      }

      const newPath = path || buildSelectionPath(question);

      setSelectionPath(newPath);

      if (location.hash.substring(1) !== question.id) {
        navigate(`?organization_id=${currentOrganization.id}#${question.id}`, { replace: true });
      }
    },
    [selectionPath, queryKey, location]
  );

  const findNextValidQuestion = useCallback((question, allQuestions) => {
    // Base case: if the question is not a clarify question, return it
    if (!question?.isClarify && !question.isCategoryQuestion) {
      return question.lastAssetFollowUp;
    }

    // If it's a clarify question, check its parent
    const parentQuestion = findQuestionById(allQuestions, question.previousQuestionId);
    if (!parentQuestion) {
      return null;
    }

    if (!parentQuestion.isClarify && !parentQuestion.isCategoryQuestion) {
      return parentQuestion;
    }

    // Recursively check the parent
    return findNextValidQuestion(parentQuestion, allQuestions);
  }, []);

  const handleBranchChange = (question) => {
    let path = null;
    if (question.isClarify || question.isCategoryQuestion) {
      path = buildSelectionPath(question);
    }
    // Find the next valid question to select, handling clarify questions recursively
    const nextQuestion = findNextValidQuestion(question, questions);
    handleSelectQuestion(nextQuestion, path);
  };

  const handleDeleteQuestion = useCallback(
    (questionId) => {
      const questionToRemove = findQuestionById(questions, questionId);
      const previousQuestion = findQuestionById(questions, questionToRemove.previousQuestionId);

      if (questionToRemove.previousQuestionId === null) {
        // if it is the first question in the chain
        // archive the conversation
        deleteConversation(questionToRemove.conversationId);
        // and redirect to the new chart page
        openNextConversation(questionToRemove.conversationId, true);
      } else if (questionToRemove.isChart) {
        // When deleting a chart question, we need to handle two cases:
        // 1. Deleting a chart refinement (previousQuestion is also a chart) → select previous chart
        // 2. Deleting the only/first chart (previousQuestion is not a chart) → clear chart mode

        let updatedQuestion = selectedQuestion.removeLastChartFollowUp();

        if (previousQuestion.isChart) {
          // Case 1: Deleting a chart refinement - select the previous chart
          updatedQuestion = updatedQuestion.setCurrentChartFollowUp(previousQuestion);
        } else if (selectedQuestion?.currentChartFollowUp?.id === questionId) {
          // Case 2: Deleting the only/first chart - clear chart mode
          updatedQuestion = updatedQuestion.set('currentChartFollowUp', null);
        }

        handleSelectQuestion(updatedQuestion);
        setSelectionPath(buildSelectionPath(updatedQuestion));
      } else {
        // Select the next sibling in line
        const index = previousQuestion.followUps.findIndex((q) => q.id === questionId);

        if (index === 0) {
          handleSelectQuestion(previousQuestion);
          setSelectionPath(buildSelectionPath(previousQuestion));
        } else {
          const previousSibling = previousQuestion.followUps.get(index - 1);
          // Find the next valid question to select, handling clarify questions recursively
          const nextQuestion = findNextValidQuestion(previousSibling, questions);
          handleSelectQuestion(nextQuestion);
          setSelectionPath(buildSelectionPath(nextQuestion));
        }
      }
      deleteQuestion(questionId);
    },
    [
      questions?.[0]?.id,
      deleteQuestion,
      handleSelectQuestion,
      selectedQuestion,
      findNextValidQuestion
    ]
  );

  // #endregion

  // Listener for pipeline_step_complete
  const handlePipelineStepComplete = useCallback(
    (data) => {
      const { questionId, isRetry, pipelineStep, answer, question } = camelizeKeys(data);

      // If the question has a parentQuestionId, add it to parent's childQuestions
      if (question?.parentQuestionId) {
        queryClient.setQueryData(queryKey, (oldQuestions) =>
          updateParentQuestionWithChild(oldQuestions, question.parentQuestionId, question)
        );
      } else {
        // Only run the original logic if question doesn't have parentQuestionId
        queryClient.setQueryData(queryKey, (oldQuestions) =>
          updateQuestionParams(oldQuestions, questionId, {
            status: pipelineStep,
            isRetry,
            answers: answer ? [answer] : []
          })
        );
      }
    },
    [queryKey, selectedQuestion]
  );

  const handleQuestionAnswered = useCallback(
    (data) => {
      const { question } = camelizeKeys(data);

      if (question.parentQuestionId !== null) {
        return;
      }

      if (currentConversation.id === question.conversationId) {
        queryClient.setQueryData(queryKey, (oldQuestions) =>
          replaceQuestion(oldQuestions, question.id, question)
        );

        if (question) {
          if (question.command !== 'chart' && question.command !== 'chart-refine') {
            const updatedQuestion = new Question(question);
            if (!updatedQuestion.isClarify && !updatedQuestion.isCategoryQuestion) {
              handleSelectQuestion(updatedQuestion);
            } else {
              // For clarify questions, we need to update the selection path so that
              // subsequent questions use this clarify question as the previousQuestionId
              const newPath = buildSelectionPath(updatedQuestion);
              setSelectionPath(newPath);
            }
          } else {
            const updatedQuestion = selectedQuestion.setCurrentChartFollowUp(
              new Question(question)
            );
            handleSelectQuestion(updatedQuestion);
          }
        }
      }
    },
    [queryKey, selectedQuestion, currentConversation.id, handleSelectQuestion, buildSelectionPath]
  );

  useEffect(() => {
    if (io) {
      io.on('pipeline_step_complete', handlePipelineStepComplete);
      io.on('question_answered', handleQuestionAnswered);
    }

    return () => {
      if (io) {
        io.off('pipeline_step_complete', handlePipelineStepComplete);
        io.off('question_answered', handleQuestionAnswered);
      }
    };
  }, [io, handlePipelineStepComplete, handleQuestionAnswered]);

  useEffect(() => {
    if (joinRoom) {
      joinRoom('conversation', conversationId);
    }

    return () => {
      if (leaveRoom) {
        leaveRoom('conversation', conversationId);
      }
    };
  }, [leaveRoom, joinRoom, conversationId]);

  useEffect(() => {
    setIsChartMode(false);
    setSelectedQuestion(null);

    if (questions.length > 0 && conversationId) {
      if (location.hash) {
        const questionId = location.hash.substring(1);
        const question = findQuestionById(questions, questionId);

        if (question) {
          handleSelectQuestion(question);
          return;
        }
      }

      const lastParentQuestion = questions[questions.length - 1];
      handleSelectQuestion(lastParentQuestion.lastAssetFollowUp || lastParentQuestion);
    } else {
      setSelectionPath(new Map());
      setSelectedQuestion(null);
    }

    // The reason why length didn't work is because a temporary ID is used to create the question,
    // causing the effect to trigger. However, when the answer becomes available,
    // the length of the questions array remains unchanged, preventing the effect from being triggered again.
    // Question id will stay the same, so we can use it to trigger the effect.
  }, [questions?.[0]?.id, conversationId]);

  const value = useMemo(
    () => ({
      isShared: currentConversation?.isSharedWith(currentUser, users),
      isChartMode,
      isFetchingQuestions,
      setIsChartMode,
      selectionPath,
      selectedQuestion,
      setSelectedQuestion,
      sources,
      users,
      participants,
      termTranslations,
      databaseDescription,
      currentConversation,
      currentOrganization,
      currentUser,
      columnMentionItems,
      questions,
      matchingEntities,
      handleSelectEntity,
      handleShare,
      handleUserFeedback,
      handleUpdateAssetTitle,
      handleUpdateAnswer,
      handleAddQuestionToSpace,
      handleRemoveQuestionFromSpace,
      handleCreateQuestion,
      handleSelectQuestion,
      handleDeleteQuestion,
      handleRetryQuestion,
      handleUpdateConversation: updateConversation,
      spaces,
      assets,
      createSpace,
      metrics,
      handleCreateAnswer,
      handleBranchChange,
      findEntityMatches: handleFindEntityMatches,
      accessToken
    }),
    [
      isChartMode,
      setIsChartMode,
      isFetchingQuestions,
      currentConversation,
      currentOrganization,
      currentUser,
      columnMentionItems,
      selectionPath,
      selectedQuestion,
      sources,
      termTranslations,
      databaseDescription,
      questions,
      participants,
      matchingEntities,
      handleSelectEntity,
      handleShare,
      handleUserFeedback,
      handleUpdateAssetTitle,
      handleUpdateAnswer,
      handleAddQuestionToSpace,
      handleRemoveQuestionFromSpace,
      handleCreateQuestion,
      handleSelectQuestion,
      handleDeleteQuestion,
      handleRetryQuestion,
      updateConversation,
      handleBranchChange,
      users,
      spaces,
      assets,
      createSpace,
      metrics,
      handleCreateAnswer,
      handleFindEntityMatches,
      accessToken
    ]
  );

  return <AssetBuilderContext.Provider value={value}>{children}</AssetBuilderContext.Provider>;
};

AssetBuilderProvider.propTypes = {
  children: PropTypes.node
};

const useAssetBuilder = () => useContext(AssetBuilderContext);

export { AssetBuilderProvider, AssetBuilderContext, useAssetBuilder };
